import { CheckCircle2 } from 'lucide-react';
import { calculateLeaderboard } from '../../utils/scoreCalculator';

const TOP3_PILLAR = [
  { bg: 'bg-mp-lime',     text: 'text-mp-darkgreen', avatar: 'bg-mp-darkgreen text-mp-lime' },
  { bg: 'bg-mp-teal',     text: 'text-white',         avatar: 'bg-white text-mp-teal' },
  { bg: 'bg-mp-bg-mint',  text: 'text-mp-darkgreen', avatar: 'bg-mp-darkgreen text-mp-bg-mint' },
];

export default function Scoreboard({ participants, question, showCorrectAnswer }) {
  const leaderboard = calculateLeaderboard(participants).slice(0, 5);

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || '?';

  const getCorrectAnswer = () => {
    if (!question || !question.answers) return null;
    return question.answers.find((a) => a.isCorrect);
  };

  return (
    <div className="font-body h-screen overflow-hidden bg-mp-bg-blue text-mp-darkgreen">
      <div className="relative flex h-full w-full flex-col overflow-hidden">
        {/* Header */}
        <div className="relative z-10 flex flex-col items-center text-center pt-8 pb-3 px-4">
          <span className="mp-eyebrow mb-1.5">Mikkel kviss · Mellomstilling</span>
          <h1 className="font-display text-mp-darkgreen text-4xl md:text-5xl tracking-tight">
            Resultattavle
          </h1>
        </div>

        {/* Correct Answer Section */}
        {showCorrectAnswer && question && (
          <div className="relative z-10 mx-auto w-full max-w-3xl px-6 mb-3">
            <div className="bg-white rounded-2xl p-5 shadow-mp-1 border border-[rgba(17,73,66,0.12)] text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle2 size={16} className="text-mp-teal" />
                <p className="font-display text-mp-teal text-[11px] uppercase tracking-[0.18em]">Rett svar</p>
              </div>
              <p className="font-body text-mp-stone-700 text-base mb-1">{question.text}</p>
              {getCorrectAnswer() && (
                <p className="font-display text-2xl md:text-3xl text-mp-darkgreen">
                  {getCorrectAnswer().text}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <div className="relative z-10 flex-1 w-full max-w-3xl mx-auto px-6 pb-8 flex flex-col justify-center">
          {leaderboard.length === 0 ? (
            <div className="text-center font-body text-mp-stone-500 text-2xl">
              Ingen deltakarar enno
            </div>
          ) : (
            <div className="space-y-2.5">
              {leaderboard.map((participant, index) => {
                const isTop3 = index < 3;
                const style = TOP3_PILLAR[index];

                return (
                  <div key={participant.id} className="animate-slide-in" style={{ animationDelay: `${index * 100}ms` }}>
                    <div
                      className={`flex items-center gap-4 rounded-2xl px-5 py-4 transition-all duration-300 ${
                        isTop3
                          ? `${style.bg} shadow-mp-2`
                          : index % 2 === 0
                          ? 'bg-white border border-[rgba(17,73,66,0.10)]'
                          : 'bg-mp-bg-lightgreen border border-[rgba(17,73,66,0.10)]'
                      }`}
                    >
                      {/* Rank Avatar */}
                      <div
                        className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-display text-2xl ${
                          isTop3 ? style.avatar : 'bg-white border-2 border-mp-darkgreen/15 text-mp-darkgreen/60'
                        }`}
                      >
                        {isTop3 ? <span>{getInitial(participant.name)}</span> : <span className="text-lg">{index + 1}</span>}
                      </div>

                      {/* Rank number for top 3 */}
                      {isTop3 && (
                        <div
                          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-display text-sm bg-white text-mp-darkgreen border border-mp-darkgreen/15`}
                        >
                          {index + 1}
                        </div>
                      )}

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-display truncate ${style?.text || 'text-mp-darkgreen'} ${
                            index === 0 ? 'text-2xl' : isTop3 ? 'text-xl' : 'text-lg'
                          }`}
                        >
                          {participant.name}
                        </p>
                      </div>

                      {/* Score */}
                      <div className="shrink-0 text-right">
                        <p
                          className={`font-display tabular-nums ${
                            isTop3 ? `text-2xl ${style.text}` : 'text-xl text-mp-magenta'
                          }`}
                        >
                          {(participant.score || 0).toLocaleString()}
                        </p>
                        <p
                          className={`text-[10px] font-display uppercase tracking-[0.16em] ${
                            isTop3 ? `${style.text} opacity-70` : 'text-mp-stone-500'
                          }`}
                        >
                          poeng
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
